import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { glossary, GlossaryTerm } from './metric-glossary';

/**
 * Tooltip body for a jargon term: plain sentence first, then how the number is
 * built and what it leaves out.
 */
export const GlossaryHelp = ({ term, extra }: { term: GlossaryTerm; extra?: string }) => {
  const entry = glossary[term];

  return (
    <Box sx={{ py: 0.5, maxWidth: 280 }}>
      <Typography variant="subtitle2" fontWeight={700} lineHeight={1.4}>
        {entry.name}
        {entry.abbr ? (
          <Box component="span" sx={{ opacity: 0.7, fontWeight: 500 }}>
            {' '}
            · {entry.abbr}
          </Box>
        ) : null}
      </Typography>

      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {entry.plain}
      </Typography>

      {entry.formula ? (
        <Typography
          variant="caption"
          component="p"
          sx={{
            mt: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'rgba(255, 255, 255, 0.12)',
            lineHeight: 1.5,
          }}
        >
          {entry.formula}
        </Typography>
      ) : null}

      {entry.excludes ? (
        <Typography variant="caption" component="p" sx={{ mt: 1, opacity: 0.85 }}>
          {entry.excludes}
        </Typography>
      ) : null}

      {extra ? (
        <Typography variant="caption" component="p" sx={{ mt: 1, opacity: 0.85 }}>
          {extra}
        </Typography>
      ) : null}
    </Box>
  );
};

/** Inline ⓘ that opens the glossary entry. Use inside a label's text flow. */
export const GlossaryIcon = ({ term, extra }: { term: GlossaryTerm; extra?: string }) => (
  <Tooltip title={<GlossaryHelp term={term} extra={extra} />} arrow>
    <InfoOutlinedIcon
      fontSize="inherit"
      sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }}
    />
  </Tooltip>
);
